/**
 * NIP d'un responsable dans l'ecran de vente : rabais au-dela du plafond, ou
 * prix modifie. La verification est celle de la connexion
 * (app/pinLogin.js verifyPinLogin) : en ligne si le poste l'est, sinon hors
 * ligne par le verificateur PBKDF2 du poste ; meme blocage progressif. Le NIP
 * ne vit que dans l'etat de ce dialogue, vide apres chaque essai.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import PinPad, { PIN_MAX_LENGTH, PIN_MIN_LENGTH, PinDots } from '../app/PinPad';
import { LOGIN_STATUS, loginMessage } from '../app/pinLogin';
import { lockoutMessage } from '../store/pinLockout';
import { palette } from '../app/theme';

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {string} props.titre
 * @param {string} props.raison ligne d'explication (« Rabais au-delà de votre limite : NIP du responsable »)
 * @param {boolean} props.exigerResponsable is_manager exige par la verification
 * @param {(droits: object) => boolean} [props.droitSuffisant] controle de droit apres un NIP accepte
 * @param {string} [props.refusDroit] message si droitSuffisant refuse
 * @param {(o: { pin: string, requireManager: boolean }) => Promise<object>} props.verifierNip
 * @param {(employe: { employee_id: number, display_name: string, rights: object }) => void} props.onAutorise
 * @param {() => void} props.onAnnuler
 * @param {(statut: string) => void} [props.onStatutPoste]
 */
export default function DialogueNip({
    open, titre, raison, exigerResponsable, droitSuffisant, refusDroit, verifierNip, onAutorise, onAnnuler, onStatutPoste,
}) {
    const [pin, setPin] = useState('');
    const [occupe, setOccupe] = useState(false);
    const [message, setMessage] = useState(null);
    const [secousse, setSecousse] = useState(0);

    useEffect(() => {
        if (!open) {
            setPin('');
            setMessage(null);
            setOccupe(false);
        }
    }, [open]);

    const valider = useCallback(async () => {
        if (occupe || pin.length < PIN_MIN_LENGTH) return;
        const saisi = pin;
        setPin('');
        setOccupe(true);
        setMessage(null);
        let resultat;
        try {
            resultat = await verifierNip({ pin: saisi, requireManager: exigerResponsable });
        } catch (_error) {
            resultat = { status: 'error' };
        }
        setOccupe(false);
        if (resultat.status === LOGIN_STATUS.OK) {
            if (droitSuffisant && !droitSuffisant(resultat.employee.rights)) {
                setSecousse((k) => k + 1);
                setMessage(refusDroit || 'Ce NIP n\'a pas ce droit.');
                return;
            }
            onAutorise(resultat.employee);
            return;
        }
        if ((resultat.status === LOGIN_STATUS.SUSPENDED || resultat.status === LOGIN_STATUS.REVOKED) && onStatutPoste) {
            onStatutPoste(resultat.status === LOGIN_STATUS.SUSPENDED ? 'DISABLED' : 'REVOKED');
            return;
        }
        setSecousse((k) => k + 1);
        if (resultat.status === LOGIN_STATUS.LOCKED || (resultat.lock && resultat.lock.locked)) {
            setMessage(lockoutMessage(resultat.lock.until - Date.now()));
            return;
        }
        setMessage(loginMessage(resultat) || 'La vérification n\'a pas abouti. Réessayez.');
    }, [occupe, pin, verifierNip, exigerResponsable, droitSuffisant, refusDroit, onAutorise, onStatutPoste]);

    const ajouter = useCallback((d) => setPin((p) => (p.length >= PIN_MAX_LENGTH ? p : p + d)), []);
    const effacer = useCallback(() => setPin((p) => p.slice(0, -1)), []);

    const clavier = useRef(null);
    clavier.current = (event) => {
        if (event.ctrlKey || event.metaKey || event.altKey || occupe) return;
        if (/^[0-9]$/.test(event.key)) {
            event.preventDefault();
            ajouter(event.key);
        } else if (event.key === 'Backspace') {
            event.preventDefault();
            effacer();
        } else if (event.key === 'Enter') {
            event.preventDefault();
            valider();
        }
    };
    useLayoutEffect(() => {
        if (!open) return undefined;
        const ecoute = (event) => clavier.current(event);
        window.addEventListener('keydown', ecoute);
        return () => window.removeEventListener('keydown', ecoute);
    }, [open]);

    return (
        <Dialog open={open} onClose={onAnnuler} disableRestoreFocus aria-labelledby="dialogue-nip-titre" data-testid="dialogue-nip">
            <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <Typography sx={{ fontSize: 18, color: palette.textSecondary }}>{raison}</Typography>
                <Typography id="dialogue-nip-titre" component="h2" sx={{ fontSize: 30, fontWeight: 800, mt: 0.5, color: palette.text }}>
                    {titre}
                </Typography>
                <PinDots length={pin.length} shakeKey={secousse} />
                <Box role="alert" data-testid="dialogue-nip-message" sx={{ minHeight: 32, mb: 1, maxWidth: 420 }}>
                    {message && <Typography sx={{ fontSize: 18, fontWeight: 700, color: palette.errorOnTint }}>{message}</Typography>}
                    {!message && occupe && <Typography sx={{ fontSize: 16, color: palette.textSecondary }}>Vérification…</Typography>}
                </Box>
                <PinPad length={pin.length} disabled={occupe} onDigit={ajouter} onBackspace={effacer} onSubmit={valider} />
                <Button variant="outlined" onClick={onAnnuler} sx={{ mt: 3, px: 6 }}>Annuler</Button>
            </Box>
        </Dialog>
    );
}
